/**
 * useUserModals — single source of truth for all user-management modal state.
 *
 * Groups together:
 *   - selectedUser (the row currently being acted on)
 *   - 6 modal-visibility flags
 *   - 5 form payloads (newPassword, banReason, resetLinkData, editUserData, newUserData)
 *
 * Exposes a flat object of state + setters compatible with the existing modal
 * components, plus a `closeAll()` helper for safety net cleanup.
 *
 * Centralising this lets useUserActions consume the close-modal setters
 * directly from the same hook, and keeps ManagePlatform.js focused on layout.
 */
import { useCallback, useMemo, useState } from 'react';

const EMPTY_EDIT_USER = { name: '', email: '', phone: '' };
const EMPTY_NEW_USER = { first_name: '', last_name: '', email: '', phone: '' };

export default function useUserModals() {
  const [selectedUser, setSelectedUser] = useState(null);

  // Visibility flags
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showBanUserModal, setShowBanUserModal] = useState(false);
  const [showResetLinkModal, setShowResetLinkModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  // Form payloads
  const [newPassword, setNewPassword] = useState('');
  const [banReason, setBanReason] = useState('');
  const [resetLinkData, setResetLinkData] = useState(null);
  const [editUserData, setEditUserData] = useState(EMPTY_EDIT_USER);
  const [newUserData, setNewUserData] = useState(EMPTY_NEW_USER);

  // Open the edit modal pre-filled from a user row
  const openEditUserModal = useCallback((user) => {
    setSelectedUser(user);
    setEditUserData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    });
    setShowEditUserModal(true);
  }, []);

  // Cancel helpers — wired to each modal's onCancel for consistency.
  const cancelEdit = useCallback(() => {
    setShowEditUserModal(false);
    setSelectedUser(null);
  }, []);
  const cancelResetPassword = useCallback(() => {
    setShowResetPasswordModal(false);
    setNewPassword('');
    setSelectedUser(null);
  }, []);
  const cancelDelete = useCallback(() => {
    setShowDeleteUserModal(false);
    setSelectedUser(null);
  }, []);
  const cancelBan = useCallback(() => {
    setShowBanUserModal(false);
    setBanReason('');
    setSelectedUser(null);
  }, []);
  const cancelAddUser = useCallback(() => {
    setShowAddUserModal(false);
    setNewUserData(EMPTY_NEW_USER);
  }, []);
  const cancelResetLink = useCallback(() => {
    setShowResetLinkModal(false);
    setResetLinkData(null);
  }, []);

  // Safety net used by parent on route changes / sign-out — closes anything left open.
  const closeAll = useCallback(() => {
    setShowResetPasswordModal(false);
    setShowDeleteUserModal(false);
    setShowEditUserModal(false);
    setShowBanUserModal(false);
    setShowResetLinkModal(false);
    setShowAddUserModal(false);
    setSelectedUser(null);
    setNewPassword('');
    setBanReason('');
    setResetLinkData(null);
    setEditUserData(EMPTY_EDIT_USER);
    setNewUserData(EMPTY_NEW_USER);
  }, []);

  // The returned object is intentionally flat so existing callsites can
  // destructure exactly what they need without nested-prop refactoring.
  return useMemo(() => ({
    // Selected row
    selectedUser, setSelectedUser,
    // Visibility
    showResetPasswordModal, setShowResetPasswordModal,
    showDeleteUserModal, setShowDeleteUserModal,
    showEditUserModal, setShowEditUserModal,
    showBanUserModal, setShowBanUserModal,
    showResetLinkModal, setShowResetLinkModal,
    showAddUserModal, setShowAddUserModal,
    // Payloads
    newPassword, setNewPassword,
    banReason, setBanReason,
    resetLinkData, setResetLinkData,
    editUserData, setEditUserData,
    newUserData, setNewUserData,
    // Helpers
    openEditUserModal,
    cancelEdit, cancelResetPassword, cancelDelete,
    cancelBan, cancelAddUser, cancelResetLink,
    closeAll,
  }), [
    selectedUser,
    showResetPasswordModal, showDeleteUserModal, showEditUserModal,
    showBanUserModal, showResetLinkModal, showAddUserModal,
    newPassword, banReason, resetLinkData, editUserData, newUserData,
    openEditUserModal,
    cancelEdit, cancelResetPassword, cancelDelete,
    cancelBan, cancelAddUser, cancelResetLink, closeAll,
  ]);
}
